import {Component, EventEmitter, Inject, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {AbstractControl} from '@angular/forms';
import {MAT_DATE_FORMATS} from '@angular/material/core';
import {MatDatepicker, MatDatepickerInputEvent} from '@angular/material/datepicker';
import {Moment} from 'moment';
import moment from 'moment-timezone';
import {Subject} from 'rxjs';
import {startWith, takeUntil} from 'rxjs/operators';
import {DATE_FORMAT_DAY} from '../../../../../../consts/date-time-format-consts';
import {ZoneDatePipe} from '../../../../../../shared/pipes/zoneDate.pipe';
import {MxFormErrorMessageService} from '../../../../services/mx-form-error-message.service';
import {MxField} from '../../services/mxField';
import {MX_TRANSLATE_SERVICE_TOKEN, MxTranslateService} from "../../../../../../shared/services/mx-translate.service";

export const RETRO_DATE_FROM = 'statDateRetro_from';
export const RETRO_DATE_TO = 'statDateRetro_to';

export const DEFAULT_FORMATS = {
    parse: {
        dateInput: 'YYYY-MM-DD',
    },
    display: {
        dateInput: 'YYYY-MM-DD',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
    },
};

export class BoDatePickerConfig {
    biggerThan?: string;
    lessThan?: string;
    selectionType?: 'multi-year' | 'month';
}

@Component({
    selector: 'mx-date-picker',
    standalone: false,
    templateUrl: './mx-date-picker.component.html',
    providers: [
        ZoneDatePipe,
        {provide: MAT_DATE_FORMATS, useValue: DEFAULT_FORMATS}
        ]
})
export class MxDatePickerComponent extends MxField implements OnInit, OnDestroy {

    public dateFormat: string = DATE_FORMAT_DAY;

    @Input() maxDate: Moment | null;
    @Input() minDate: Moment | null;
    private selectionType: 'multi-year' | 'month';

    @Output()
    dateInput: EventEmitter<MatDatepickerInputEvent<Moment>> = new EventEmitter<MatDatepickerInputEvent<Moment>>();

    private _destroy$$: Subject<null> = new Subject<null>();

    constructor(public errorService: MxFormErrorMessageService,
                @Inject(MAT_DATE_FORMATS) private formats: any,
                @Inject(MX_TRANSLATE_SERVICE_TOKEN) protected translateService: MxTranslateService) {
        super(errorService, translateService);
    }


    today(datepicker: MatDatepicker<any>, event: any): void {
        if (event.pointerType === 'mouse') {
            this.form.get([this.name]).setValue(moment());
            datepicker.close();
        }
    }

    public onOpen(datepickerFooter: HTMLDivElement): void {
        const matCalendar = document.getElementsByClassName('mat-datepicker-content')[0] as HTMLElement;
        matCalendar.appendChild(datepickerFooter);
    }

    ngOnInit(): void {
        let componentControl: AbstractControl = this.form.controls[this.name];
        if (componentControl.value) {
            componentControl.setValue(this.getPredefinedValue(componentControl.value));
        }

        if (this.name === RETRO_DATE_TO) {
            let dependControl = this.form.controls[RETRO_DATE_FROM];
            dependControl.valueChanges.pipe(
                startWith(dependControl.value),
                takeUntil(this._destroy$$)
            ).subscribe(value => {
                if (value) {
                    this.maxDate = moment(value).add(31, 'days');
                    if (dependControl.value < componentControl.value) {
                        componentControl.reset();
                    }
                }
            });
        }

        if (this.fieldConfig && this.fieldConfig.datePickerConfig) {
            if (this.fieldConfig.datePickerConfig.biggerThan) {
                let dependControl = this.form.controls[this.fieldConfig.datePickerConfig.biggerThan];
                dependControl.valueChanges.pipe(
                    startWith(dependControl.value),
                    takeUntil(this._destroy$$)
                ).subscribe(value => {
                    let newMinDate: Moment | null = this.toStartOfDay(value);
                    let currentValue: Moment | null = this.toStartOfDay(componentControl.value);
                    this.minDate = newMinDate;
                    if (newMinDate && currentValue && currentValue.isBefore(newMinDate)) {
                        componentControl.reset();
                    }
                });
            }
            if (this.fieldConfig.datePickerConfig.selectionType) {
                this.selectionType = this.fieldConfig.datePickerConfig.selectionType;
                this.formats.display.dateInput = 'MM-YYYY';
                this.formats.parse.dateInput = 'MM-YYYY';
            }
        }
    }

    /**
     * The picker works with day precision, so bounds are normalized to midnight,
     * otherwise a 'NOW' based value would exclude the current day itself.
     */
    private toStartOfDay(value: any): Moment | null {
        if (!value) {
            return null;
        }
        let date: Moment = this.getPredefinedValue(value);
        return date.isValid() ? date.startOf('day') : null;
    }

    private getPredefinedValue(value: string): Moment {
        switch (value) {
            case 'NOW':
                return moment();
            default:
                return moment(value);
        }
    }

    onDateChanged($event: MatDatepickerInputEvent<Moment>): void {
        this.dateInput.emit($event);
    }

    ngOnDestroy(): void {
        this._destroy$$.next(null);
    }

    getStartView(): 'month' | 'year' | 'multi-year' {
        if (this.selectionType) {
            return this.selectionType;
        } else {
            return 'month';
        }
    }

    chosenMonthHandler(normilizedMonth: Moment, datepicker: MatDatepicker<any>): void {
        if (this.selectionType === 'multi-year') {
            let componentControl: AbstractControl = this.form.controls[this.name];
            componentControl.setValue(normilizedMonth.toDate());
            datepicker.close();
        }
    }
}
